import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { loadErrorMessage, settledList } from '../../utils/loadState'
import { getThread, getThreadExecutions, runThread, getCustomer } from '../../api'
import './ThreadExecution.css'

export default function ThreadExecution() {
  const { threadId, executionId } = useParams()
  const [thread, setThread] = useState(null)
  const [executions, setExecutions] = useState([])
  const [currentExecution, setCurrentExecution] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  // A failed read is an error with a Retry -- never a page of "Pending" steps, and never
  // the made-up execution log this page used to fill the gap with (item 201).
  const [threadError, setThreadError] = useState(null)
  const [execsError, setExecsError] = useState(null)
  const [loadTry, setLoadTry] = useState(0)
  // Re-run: its outcome and any failure are shown (item 201 leftovers). The button used to
  // fire runThread and drop both -- and it sent no customer_id, which the run endpoint
  // requires, so every click was refused (422) with nothing on screen.
  const [rerunning, setRerunning] = useState(false)
  const [rerunResult, setRerunResult] = useState(null)
  const pollRef = useRef(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const [t, execs] = await Promise.allSettled([
          getThread(threadId),
          getThreadExecutions(threadId),
        ])
        if (t.status === 'fulfilled') {
          setThread(t.value)
          setThreadError(null)
          if (t.value.customer_id) {
            // The customer's name only labels the page; without it the id is shown.
            try {
              const c = await getCustomer(t.value.customer_id)
              setCustomer(c)
            } catch (err) { console.warn('Customer name not loaded:', err) }
          }
        } else {
          setThreadError(loadErrorMessage(t.reason))
        }
        const ex = settledList(execs, 'executions')
        setExecsError(ex.error)
        const list = ex.items || []
        setExecutions(list)
        if (executionId) {
          setCurrentExecution(list.find(e => e.id === executionId || e.execution_id === executionId) || list[0] || null)
        } else {
          setCurrentExecution(list[0] || null)
        }
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [threadId, executionId, loadTry])
  const retry = () => setLoadTry(n => n + 1)

  const rerun = async () => {
    setRerunResult(null)
    if (!thread?.customer_id) {
      setRerunResult({ ok: false, message: "Re-run needs this thread's customer, which has not loaded." })
      return
    }
    setRerunning(true)
    try {
      const res = await runThread(threadId, { customer_id: thread.customer_id })
      const failed = !res || res.error || ['failed', 'error'].includes(res.status)
      if (failed) {
        setRerunResult({ ok: false, message: `Re-run ${res?.status || 'failed'}: ${res?.error || 'no reason given'}` })
      } else {
        const fr = res.final_result || {}
        const steps = fr.total_steps != null ? ` · ${fr.successful_steps ?? 0} of ${fr.total_steps} steps succeeded` : ''
        setRerunResult({ ok: true, message: `Re-run ${res.status || 'finished'}${steps}` })
      }
      retry()   // re-read the executions so the new run is listed
    } catch (err) {
      setRerunResult({ ok: false, message: `Re-run failed: ${loadErrorMessage(err)}` })
    } finally {
      setRerunning(false)
    }
  }

  const threadName = thread?.name || thread?.title || 'Thread Execution'
  const companyName = customer?.company_name || thread?.customer_id || '—'
  const steps = thread?.steps || currentExecution?.steps || []

  // Determine step statuses from execution
  const getStepStatus = (stepIndex) => {
    if (!currentExecution) return 'pending'
    const execStatus = currentExecution.status || 'pending'
    const completedSteps = currentExecution.completed_steps || currentExecution.current_step || 0
    if (stepIndex < completedSteps) return 'complete'
    if (stepIndex === completedSteps && execStatus === 'running') return 'running'
    if (execStatus === 'error' && stepIndex === completedSteps) return 'error'
    return 'pending'
  }

  const totalSteps = steps.length
  const completedCount = currentExecution?.completed_steps || currentExecution?.current_step || 0
  const progress = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0
  const execStatus = currentExecution?.status || 'pending'

  // Only what the thread and its execution actually carry: no placeholder steps and no
  // invented log lines when there are none.
  const displaySteps = steps
  const outputLog = currentExecution?.log || currentExecution?.output || []

  const breadcrumbs = [
    { label: 'Threads', href: '/threads' },
    { label: threadName, href: `/threads/${threadId}` },
    { label: 'Execution' },
  ]
  const navActions = (
    <>
      <Link to={`/threads/${threadId}`} className="nav-btn">&larr; Thread Detail</Link>
      <button className="nav-btn primary" onClick={rerun} disabled={rerunning}>{rerunning ? 'Running…' : 'Re-run'}</button>
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="te-loading"><div className="te-spinner" /><p>Loading execution&hellip;</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="te-page">
        {threadError && <LoadError what="this thread" error={threadError} onRetry={retry} />}
        {execsError && <LoadError what="this thread's executions" error={execsError} onRetry={retry} />}
        {rerunResult && (
          <div className="te-rerun-result" role={rerunResult.ok ? 'status' : 'alert'}
            style={{ color: rerunResult.ok ? 'var(--green-dark)' : 'var(--red)', padding: '8px 0' }}>
            {rerunResult.message}
          </div>
        )}
        {/* Header */}
        <div className="te-header">
          <div>
            <h1 className="te-title">{threadName}</h1>
            <p className="te-subtitle">{companyName} &middot; {displaySteps.length} steps &middot; {currentExecution?.created_at ? new Date(currentExecution.created_at).toLocaleString() : 'Not run yet'}</p>
          </div>
          <div className="te-header-right">
            <span className={`te-status-badge ${execStatus}`}>
              {execStatus === 'running' ? 'Running' : execStatus === 'complete' ? 'Complete' : execStatus === 'error' ? 'Error' : 'Pending'}
            </span>
          </div>
        </div>

        <div className="te-body">
          {/* Left: Steps + Progress */}
          <div className="te-steps-panel">
            {/* Progress */}
            <div className="te-progress-section">
              <div className="te-progress-bar">
                <div className="te-progress-fill" style={{ width: `${progress}%`, background: execStatus === 'error' ? 'var(--red)' : undefined }}></div>
              </div>
              <div className="te-progress-label">{progress}% &middot; {completedCount} of {displaySteps.length} steps</div>
            </div>

            {/* Step List */}
            <div className="te-step-list">
              {displaySteps.length === 0 && !threadError && (
                <div className="te-log-empty">No steps recorded for this thread.</div>
              )}
              {displaySteps.map((step, i) => {
                const status = getStepStatus(i)
                return (
                  <div key={i} className={`te-step-item ${status === 'running' ? 'active' : ''} ${status === 'error' ? 'error-bg' : ''}`}>
                    <div className={`te-step-number ${status}`}>
                      {status === 'complete' ? '\u2713' : status === 'error' ? '\u2717' : i + 1}
                    </div>
                    <div className="te-step-content">
                      <div className="te-step-text">{step.name || step.title || step.description || `Step ${i + 1}`}</div>
                      {step.description && step.name && <div className="te-step-detail">{step.description}</div>}
                      {status === 'running' && <div className="te-step-status running">Running&hellip;</div>}
                      {status === 'complete' && <div className="te-step-status complete">Complete</div>}
                      {status === 'error' && <div className="te-step-status error">Failed</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right: Output Log */}
          <div className="te-output-panel">
            <div className="te-output-header">Execution Log</div>
            <div className="te-output-scroll">
              {Array.isArray(outputLog) && outputLog.map((entry, i) => (
                <div key={i} className="te-log-entry">
                  <span className="te-log-time">{entry.time || entry.timestamp || ''}</span>
                  <span className="te-log-step">Step {(entry.step ?? 0) + 1}</span>
                  <span className="te-log-msg">{entry.message || entry.text || entry}</span>
                </div>
              ))}
              {(!Array.isArray(outputLog) || outputLog.length === 0) && (
                <div className="te-log-empty">No execution output available yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* Past Executions */}
        {executions.length > 1 && (
          <div className="te-past-section">
            <div className="te-subsection-title">Past Executions</div>
            <div className="te-past-list">
              {executions.slice(0, 10).map((ex, i) => (
                <div key={i} className={`te-past-item ${currentExecution?.id === ex.id ? 'selected' : ''}`} onClick={() => setCurrentExecution(ex)}>
                  <span className={`te-past-status ${ex.status || 'complete'}`}>{ex.status || 'complete'}</span>
                  <span className="te-past-date">{ex.created_at ? new Date(ex.created_at).toLocaleString() : `Run ${i + 1}`}</span>
                  <span className="te-past-duration">{ex.duration || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
